import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminTopBar from "./AdminTopBar";
import AdminPagination from "./AdminPagination";
import { BusyTable } from "../components/TableBusyOverlay";
import usePagedListUi from "../hooks/usePagedListUi";
import { ADMIN_PAGE_SIZE, totalPagesFromCount } from "../lib/adminPagination";
import { adminPrimaryBtn, adminStatusPill } from "./adminUi";
import {
  SUPPORT_TICKET_SOURCES,
  SUPPORT_TICKET_STATUSES,
  filterAdminSupportTickets,
  isRefundRequestType,
  listSupportTicketsPage,
  updateSupportTicketStatus,
} from "../lib/supportTickets";
import {
  denyRefundRequest,
  markOrderRefunded,
} from "../lib/refundOrders";
import {
  SUPPORT_EMAIL_TYPES,
  notifyBuyerRefundUpdate,
} from "../lib/supportEmailNotify";
import { buildOrderCode, formatMoney, statusLabel } from "./ordersPage";
import { supabase } from "../supabaseClient";
import { createAdminNotification } from "./notificationsService";

const PAGE_SIZE = ADMIN_PAGE_SIZE;

const statusPillClass = (status) => {
  if (status === "Pending") return "bg-amber-100 text-amber-700";
  if (status === "Open") return "bg-red-100 text-red-700";
  return "bg-green-100 text-green-700";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

const Support = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(SUPPORT_TICKET_SOURCES.BOOKING_APP);
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [tabCounts, setTabCounts] = useState({ booking_app: 0, website: 0 });
  const listTopRef = useRef(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [linkedOrder, setLinkedOrder] = useState(null);
  const [draftStatus, setDraftStatus] = useState("Open");
  const [draftNotes, setDraftNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [modalError, setModalError] = useState("");
  const [refundActionConfirm, setRefundActionConfirm] = useState(null); // "mark" | "deny" | null

  const loadTickets = useCallback(async () => {
    setLoading(true);
    const [{ rows, count }, bookingCount, websiteCount] = await Promise.all([
      listSupportTicketsPage({
        page: currentPage,
        pageSize: PAGE_SIZE,
        source: activeTab,
        status: statusFilter,
      }),
      supabase.from("support_tickets").select("id", { count: "exact", head: true }).eq("source", SUPPORT_TICKET_SOURCES.BOOKING_APP),
      supabase.from("support_tickets").select("id", { count: "exact", head: true }).eq("source", SUPPORT_TICKET_SOURCES.WEBSITE),
    ]);
    setTickets(rows);
    setTotalCount(count);
    setTabCounts({
      booking_app: Number(bookingCount.count) || 0,
      website: Number(websiteCount.count) || 0,
    });
  }, [activeTab, currentPage, statusFilter]);

  useEffect(() => {
    const fetchTickets = async () => {
      setError("");
      try {
        await loadTickets();
      } catch (loadError) {
        setError(loadError.message || "Unable to load support tickets.");
        setTickets([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [loadTickets]);

  const filteredTickets = useMemo(
    () => filterAdminSupportTickets(tickets, { search, status: "All", source: activeTab }),
    [tickets, search, activeTab]
  );
  const totalPages = totalPagesFromCount(totalCount, PAGE_SIZE);
  const paginatedTickets = filteredTickets;
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, activeTab]);

  const loadLinkedOrder = async (paymentOrderId) => {
    if (!paymentOrderId) {
      setLinkedOrder(null);
      return;
    }

    const { data: order, error: orderError } = await supabase
      .from("payment_orders")
      .select("id, amount, status, event_id, paid_at")
      .eq("id", paymentOrderId)
      .maybeSingle();

    if (orderError || !order) {
      setLinkedOrder({
        id: paymentOrderId,
        orderCode: buildOrderCode(paymentOrderId),
        missing: true,
      });
      return;
    }

    const [{ data: event }, { data: ticketsData }] = await Promise.all([
      order.event_id
        ? supabase.from("events").select("id, title, date").eq("id", order.event_id).maybeSingle()
        : Promise.resolve({ data: null }),
      supabase
        .from("tickets")
        .select("id, is_validated, refunded_at")
        .eq("payment_order_id", order.id),
    ]);

    const ticketRows = Array.isArray(ticketsData) ? ticketsData : [];
    setLinkedOrder({
      id: order.id,
      orderCode: buildOrderCode(order.id),
      amount: Number(order.amount || 0),
      status: order.status,
      eventTitle: event?.title || "Event",
      eventDate: event?.date || "",
      ticketCount: ticketRows.length,
      usedCount: ticketRows.filter((ticket) => ticket.is_validated).length,
      missing: false,
    });
  };

  const openManage = async (ticket) => {
    setSelectedTicket(ticket);
    setDraftStatus(ticket.status);
    setDraftNotes(ticket.adminNotes || "");
    setModalError("");
    setLinkedOrder(null);
    if (isRefundRequestType(ticket.issueType) && ticket.paymentOrderId) {
      await loadLinkedOrder(ticket.paymentOrderId);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!selectedTicket) return;

    setSaving(true);
    setModalError("");

    try {
      const updated = await updateSupportTicketStatus(selectedTicket.id, {
        status: draftStatus,
        adminNotes: draftNotes,
      });
      setTickets((current) => current.map((ticket) => (ticket.id === updated.id ? updated : ticket)));
      setSelectedTicket(null);
    } catch (saveError) {
      setModalError(saveError.message || "Unable to update this ticket.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenInOrders = () => {
    if (!selectedTicket?.paymentOrderId) return;
    navigate(`/admin/orders?order=${encodeURIComponent(selectedTicket.paymentOrderId)}`);
  };

  const requestMarkRefundedFromSupport = () => {
    if (!selectedTicket?.paymentOrderId || actionBusy) return;
    setRefundActionConfirm("mark");
  };

  const requestDenyFromSupport = () => {
    if (!selectedTicket || actionBusy) return;
    if (!draftNotes.trim()) {
      setModalError("Add an admin note before denying this refund request.");
      return;
    }
    setModalError("");
    setRefundActionConfirm("deny");
  };

  const confirmMarkRefundedFromSupport = async () => {
    if (!selectedTicket?.paymentOrderId || actionBusy) return;

    setActionBusy(true);
    setModalError("");
    try {
      await markOrderRefunded(selectedTicket.paymentOrderId, draftNotes || "Order marked refunded from Support.");
      await createAdminNotification({
        title: "Order refunded",
        message: `${linkedOrder?.orderCode || selectedTicket.paymentOrderId} marked refunded from Support.`,
        meta: "Orders",
      });

      const notifyResult = await notifyBuyerRefundUpdate({
        type: SUPPORT_EMAIL_TYPES.REFUND_APPROVED,
        toEmail: selectedTicket.email,
        orderId: selectedTicket.paymentOrderId,
        orderCode: linkedOrder?.orderCode || buildOrderCode(selectedTicket.paymentOrderId),
        eventTitle: linkedOrder?.eventTitle || "",
        supportTicketId: selectedTicket.id,
        paymentOrderId: selectedTicket.paymentOrderId,
      }).catch((notifyError) => {
        console.error("Refund approved email failed:", notifyError);
        return { ok: false, error: notifyError.message || "Unable to send refund email." };
      });
      if (!notifyResult?.ok) {
        setModalError(notifyResult?.skipped
          ? "Order refunded, but buyer email is missing so no email was sent."
          : `Order refunded, but email failed: ${notifyResult?.error || "check function logs."}`);
      }

      setRefundActionConfirm(null);
      await loadTickets();
      setSelectedTicket(null);
    } catch (actionError) {
      setModalError(actionError.message || "Unable to mark this order refunded.");
    } finally {
      setActionBusy(false);
    }
  };

  const confirmDenyFromSupport = async () => {
    if (!selectedTicket || actionBusy) return;
    const note = draftNotes.trim();
    if (!note) {
      setModalError("Add an admin note before denying this refund request.");
      return;
    }

    setActionBusy(true);
    setModalError("");
    try {
      await denyRefundRequest(selectedTicket.id, note);

      const notifyResult = await notifyBuyerRefundUpdate({
        type: SUPPORT_EMAIL_TYPES.REFUND_DENIED,
        toEmail: selectedTicket.email,
        orderId: selectedTicket.paymentOrderId,
        orderCode: linkedOrder?.orderCode || buildOrderCode(selectedTicket.paymentOrderId),
        eventTitle: linkedOrder?.eventTitle || "",
        adminNote: note,
        supportTicketId: selectedTicket.id,
        paymentOrderId: selectedTicket.paymentOrderId,
      }).catch((notifyError) => {
        console.error("Refund denied email failed:", notifyError);
        return { ok: false, error: notifyError.message || "Unable to send refund email." };
      });
      if (!notifyResult?.ok) {
        setModalError(notifyResult?.skipped
          ? "Request denied, but buyer email is missing so no email was sent."
          : `Request denied, but email failed: ${notifyResult?.error || "check function logs."}`);
      }

      setRefundActionConfirm(null);
      await loadTickets();
      setSelectedTicket(null);
    } catch (actionError) {
      setModalError(actionError.message || "Unable to deny this refund request.");
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f3f3] text-gray-900">
      <AdminTopBar />

      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto min-w-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-5">
            <h1 className="text-2xl md:text-[26px] font-black text-gray-900 tracking-tight uppercase italic min-w-0">Support Tickets</h1>

            <div className="relative w-full sm:w-auto sm:max-w-xs sm:min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search ticket ID or customer name..."
                className="w-full h-9 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-xs text-gray-700"
              />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 md:p-6" ref={listTopRef}>
            {error ? (
              <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
            ) : null}

            <div className="flex flex-wrap gap-4 sm:gap-6 border-b border-gray-200 mb-4">
              {[
                { key: SUPPORT_TICKET_SOURCES.BOOKING_APP, label: `Booking App (${tabCounts.booking_app})` },
                { key: SUPPORT_TICKET_SOURCES.WEBSITE, label: `Ticketing App (${tabCounts.website})` },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.key);
                    setStatusFilter("All");
                    setCurrentPage(1);
                  }}
                  className={`pb-2 text-sm ${
                    activeTab === tab.key
                      ? "text-gray-900 border-b-2 border-red-600"
                      : "text-gray-500"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <BusyTable show={showSkeleton} tone="admin">
            <div className="rounded-lg border border-gray-200 overflow-x-auto custom-scrollbar">
              <table className="w-full min-w-[820px] table-fixed border-collapse text-sm">
                <thead>
                  <tr className="bg-black text-white text-sm">
                    <th className="px-4 py-3 text-left">Ticket ID</th>
                    <th className="px-4 py-3 text-left">Subject</th>
                    <th className="px-4 py-3 text-left">Customer Name</th>
                    <th className="px-4 py-3 text-left">
                      <div className="inline-flex items-center gap-2">
                        <span>Status</span>
                        <select
                          value={statusFilter}
                          onChange={(event) => {
                            setStatusFilter(event.target.value);
                            setCurrentPage(1);
                          }}
                          className="h-6 rounded border border-gray-500 bg-black px-1 text-[11px] text-white focus:outline-none"
                        >
                          <option value="All">All</option>
                          <option value={SUPPORT_TICKET_STATUSES.OPEN}>Open</option>
                          <option value={SUPPORT_TICKET_STATUSES.PENDING}>Pending</option>
                          <option value={SUPPORT_TICKET_STATUSES.RESOLVED}>Resolved</option>
                        </select>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left">Last Updated</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTickets.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-12 text-center text-sm text-gray-400">{loading ? "Loading..." : "No support tickets found."}</td>
                    </tr>
                  ) : (
                    paginatedTickets.map((item) => (
                      <tr key={item.id} className="border-t border-gray-100 text-sm">
                        <td className="px-4 py-4 font-medium text-gray-900">
                          <div className="flex flex-col gap-1">
                            <span>{item.ticketCode}</span>
                            {isRefundRequestType(item.issueType) ? (
                              <span className="inline-flex w-fit rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700">
                                Refund
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-gray-600">{item.subject}</td>
                        <td className="px-4 py-4 text-gray-600">{item.customer}</td>
                        <td className="px-4 py-4">
                          <span className={`${adminStatusPill} ${statusPillClass(item.status)}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-gray-600">{formatDate(item.updatedAt)}</td>
                        <td className="px-4 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => openManage(item)}
                            className={adminPrimaryBtn}
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            </BusyTable>

            <AdminPagination
              className="mt-6"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
            />
          </div>
        </div>
      </div>

      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={handleSave}
            className="relative w-full max-w-[92vw] sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
          >
            <button
              type="button"
              onClick={() => {
                setSelectedTicket(null);
                setRefundActionConfirm(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close manage ticket"
            >
              <X size={18} />
            </button>

            <h2 className="pr-8 text-sm font-semibold text-gray-900">Manage ticket: {selectedTicket.ticketCode}</h2>

            <div className="mt-4 space-y-3 text-sm">
              <div>
                <p className="text-xs text-gray-400 mb-1">Customer</p>
                <p className="text-gray-800">{selectedTicket.customer}</p>
                {selectedTicket.email ? <p className="text-xs text-gray-500">{selectedTicket.email}</p> : null}
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Issue type</p>
                <p className="text-gray-800">{selectedTicket.issueType}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Subject</p>
                <p className="text-gray-800">{selectedTicket.subject}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Details</p>
                <p className="whitespace-pre-wrap break-words text-gray-800">{selectedTicket.description}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Source</p>
                <p className="text-gray-800">{selectedTicket.source === "website" ? "Ticketing App" : "Booking App"}</p>
              </div>

              {isRefundRequestType(selectedTicket.issueType) && selectedTicket.paymentOrderId ? (
                <div className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-3 space-y-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-violet-800">Linked order</p>
                  {linkedOrder?.missing ? (
                    <p className="text-xs text-violet-900">{linkedOrder.orderCode} (order details unavailable)</p>
                  ) : linkedOrder ? (
                    <>
                      <p className="text-sm text-violet-950 font-semibold">{linkedOrder.orderCode}</p>
                      <p className="text-xs text-violet-900">{linkedOrder.eventTitle}{linkedOrder.eventDate ? ` · ${linkedOrder.eventDate}` : ""}</p>
                      <p className="text-xs text-violet-900">
                        {formatMoney(linkedOrder.amount)} · {statusLabel(linkedOrder.status)} · {linkedOrder.ticketCount} ticket{linkedOrder.ticketCount === 1 ? "" : "s"}
                        {linkedOrder.usedCount ? ` (${linkedOrder.usedCount} used)` : ""}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-violet-900">Loading order...</p>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                      <button
                        type="button"
                        onClick={handleOpenInOrders}
                        className="h-8 px-3 rounded border border-violet-300 bg-white text-xs font-bold text-violet-900"
                      >
                        Open in Orders
                      </button>
                      {selectedTicket.status !== SUPPORT_TICKET_STATUSES.RESOLVED
                        && String(linkedOrder?.status || "").toLowerCase() === "paid" ? (
                        <button
                          type="button"
                          onClick={requestMarkRefundedFromSupport}
                          disabled={actionBusy}
                          className="h-8 px-3 rounded bg-[#8b0000] text-xs font-bold text-white disabled:opacity-60"
                        >
                          Mark order refunded
                        </button>
                      ) : null}
                      {selectedTicket.status !== SUPPORT_TICKET_STATUSES.RESOLVED ? (
                        <button
                          type="button"
                          onClick={requestDenyFromSupport}
                          disabled={actionBusy}
                          className="h-8 px-3 rounded border border-gray-300 bg-white text-xs font-bold text-gray-800 disabled:opacity-60"
                        >
                          Deny request
                        </button>
                      ) : null}
                  </div>
                </div>
              ) : null}

              <div>
                <label className="text-xs text-gray-400 mb-1 block" htmlFor="support-status">Status</label>
                <select
                  id="support-status"
                  value={draftStatus}
                  onChange={(event) => setDraftStatus(event.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm text-gray-700"
                >
                  <option value={SUPPORT_TICKET_STATUSES.OPEN}>Open</option>
                  <option value={SUPPORT_TICKET_STATUSES.PENDING}>Pending</option>
                  <option value={SUPPORT_TICKET_STATUSES.RESOLVED}>Resolved</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block" htmlFor="support-notes">Admin notes</label>
                <textarea
                  id="support-notes"
                  value={draftNotes}
                  onChange={(event) => setDraftNotes(event.target.value)}
                  rows={4}
                  className="w-full border rounded px-2 py-1 text-sm text-gray-800"
                />
              </div>
            </div>

            {modalError ? <p className="mt-3 text-sm text-red-700">{modalError}</p> : null}

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setSelectedTicket(null);
                  setRefundActionConfirm(null);
                }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || actionBusy}
                className="rounded-md bg-[#8b0000] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#6f0000] disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      {refundActionConfirm && selectedTicket ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-[92vw] sm:max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl">
            <h3 className="mb-2 text-base font-black uppercase tracking-tight text-gray-900">
              {refundActionConfirm === "mark" ? "Mark order refunded?" : "Deny refund request?"}
            </h3>
            <p className="mb-4 text-sm text-gray-600">
              {refundActionConfirm === "mark"
                ? `Mark ${linkedOrder?.orderCode || "this order"} refunded? Tickets will stop working. Money return in PayMongo stays manual.`
                : `Deny refund request ${selectedTicket.ticketCode}? The linked order will stay paid.`}
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setRefundActionConfirm(null)}
                disabled={actionBusy}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={refundActionConfirm === "mark" ? confirmMarkRefundedFromSupport : confirmDenyFromSupport}
                disabled={actionBusy}
                className="rounded-lg bg-brand-maroon px-4 py-2 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {actionBusy
                  ? "Working..."
                  : refundActionConfirm === "mark"
                    ? "Yes, mark refunded"
                    : "Yes, deny request"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Support;
